/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { SEO } from "@/components/SEO";
import { SpecLabel } from "@/components/Section";

export default function CookiePolicyPage() {
    return (
        <>
            <SEO
                title="Cookie Policy"
                description="How Elevara Systems uses cookies and similar tracking technologies on theelevara.com. Last updated June 8, 2026."
                path="/cookies"
                noindex
            />
            <div
                className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16"
                data-testid="cookies-page"
            >
                <SpecLabel>Legal</SpecLabel>
                <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#0a0b0d]">
                    Cookie Policy
                </h1>
                <p className="mt-3 text-sm text-[#4a5562] font-display font-bold uppercase tracking-[0.18em]">
                    Last updated: June 8, 2026
                </p>
            </div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                <article
                    data-testid="legal-content"
                    className="rounded-2xl border-2 border-[#0a0b0d]/8 bg-white p-6 sm:p-10 lg:p-14 max-w-4xl mx-auto leading-relaxed text-[#0a0b0d]"
                >
                    <h2 className="font-display text-2xl font-extrabold mt-0">1. What Cookies Are</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Cookies are small text files that a website places on
                        your device when you visit. They let the site remember
                        your actions and preferences (such as login, language,
                        font size, and other display preferences) over a period
                        of time, so you don't have to keep re-entering them
                        whenever you come back to the site or browse from one
                        page to another. This Cookie Policy explains how
                        Elevara Systems ("we", "us", "our") uses cookies and
                        similar tracking technologies on theelevara.com.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">2. Types of Cookies We Use</h2>
                    <p className="mt-3 text-[#4a5562]">
                        We group the cookies and similar technologies used on
                        our site into four categories:
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        a. Strictly Necessary Cookies
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Required for the website to function. They enable core
                        features like page navigation, secure areas, and
                        loading session state. The site cannot work properly
                        without them. These cookies do not store personally
                        identifying information.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        b. Performance & Analytics Cookies
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Help us understand how visitors interact with our site
                        by collecting and reporting information anonymously —
                        for example which pages are visited most, time on page,
                        and how visitors moved through the site. This data
                        helps us improve site performance and content.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        c. Functional Cookies
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Allow our site to remember choices you make (such as
                        your preferred language or region) and provide
                        enhanced, personalized features. They may also be used
                        to deliver functionality you have requested, such as
                        the live chat widget on our site.
                    </p>

                    <h3 className="font-display text-lg font-extrabold mt-6 text-[#0a0b0d]">
                        d. Marketing & Tracking Cookies
                    </h3>
                    <p className="mt-2 text-[#4a5562]">
                        Used to identify visitors across different websites,
                        track campaign performance, and measure the
                        effectiveness of our advertising. These cookies may be
                        set by us or by third-party advertising partners.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">3. Third-Party Services</h2>
                    <p className="mt-3 text-[#4a5562]">
                        We use a small number of trusted third-party services
                        that set their own cookies or use similar tracking
                        technologies on our site:
                    </p>
                    <ul className="mt-3 space-y-2 text-[#4a5562] list-disc pl-6">
                        <li>
                            <strong className="text-[#0a0b0d]">LeadConnector chat widget</strong>{" "}
                            — provides the live chat bubble on our site. May
                            set session cookies to maintain chat continuity
                            across pages.
                        </li>
                        <li>
                            <strong className="text-[#0a0b0d]">Elevara internal tracking</strong>{" "}
                            — first-party tracking on brand.theelevara.com that
                            measures campaign attribution and on-site
                            engagement. No personally identifying information
                            is sold or shared with third parties.
                        </li>
                        <li>
                            <strong className="text-[#0a0b0d]">Google Fonts</strong>{" "}
                            — used to render the Inter typeface. Google may
                            log the request as part of standard CDN
                            operations.
                        </li>
                        <li>
                            <strong className="text-[#0a0b0d]">Unsplash CDN</strong>{" "}
                            — serves imagery used on the site. May log
                            requests as part of standard CDN operations.
                        </li>
                    </ul>

                    <h2 className="font-display text-2xl font-extrabold mt-10">4. Managing & Disabling Cookies</h2>
                    <p className="mt-3 text-[#4a5562]">
                        You can control and/or delete cookies as you wish. You
                        can delete all cookies that are already on your device
                        and you can set most browsers to prevent them from
                        being placed. Refer to your browser's help section for
                        instructions:
                    </p>
                    <ul className="mt-3 space-y-1 text-[#4a5562] list-disc pl-6">
                        <li>Chrome — Settings → Privacy and security → Cookies and other site data</li>
                        <li>Safari — Preferences → Privacy → Manage Website Data</li>
                        <li>Firefox — Settings → Privacy &amp; Security → Cookies and Site Data</li>
                        <li>Edge — Settings → Privacy, search, and services → Cookies and site permissions</li>
                    </ul>
                    <p className="mt-3 text-[#4a5562]">
                        If you disable cookies, some areas of the site may not
                        work as intended.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">5. Do Not Track</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Some browsers offer a "Do Not Track" (DNT) signal.
                        Because there is not yet a common understanding of how
                        to interpret DNT signals, our site does not currently
                        respond to them. You can still manage cookies and
                        tracking using the browser controls described above.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">6. Changes to This Policy</h2>
                    <p className="mt-3 text-[#4a5562]">
                        We may update this Cookie Policy from time to time to
                        reflect changes in technology, regulation, or our
                        business operations. When we do, we will update the
                        "Last updated" date at the top of this page. Continued
                        use of the site after changes constitutes acceptance
                        of the updated policy.
                    </p>

                    <h2 className="font-display text-2xl font-extrabold mt-10">7. Contact</h2>
                    <p className="mt-3 text-[#4a5562]">
                        Questions about this Cookie Policy?
                    </p>
                    <p className="mt-3 text-[#4a5562]">
                        Elevara Systems |{" "}
                        <a
                            href="mailto:contact@theelevara.com"
                            className="text-[#188bf6] font-display font-bold hover:underline"
                        >
                            contact@theelevara.com
                        </a>{" "}
                        |{" "}
                        <a
                            href="tel:+19418075580"
                            className="text-[#188bf6] font-display font-bold hover:underline"
                        >
                            941-807-5580
                        </a>{" "}
                        | theelevara.com
                    </p>
                </article>
            </div>
        </>
    );
}
